import { jsx as _jsx } from "react/jsx-runtime";
import Autocomplete, { autocompleteClasses, } from "@mui/material/Autocomplete";
import useMediaQuery from "@mui/material/useMediaQuery";
import ListSubheader from "@mui/material/ListSubheader";
import Popper from "@mui/material/Popper";
import { useTheme, styled } from "@mui/material/styles";
import { VariableSizeList } from "react-window";
import Typography from "@mui/material/Typography";
import { createContext, forwardRef, useContext, useEffect, useMemo, useRef, } from "react";
import { MenuItem } from "@mui/material";
/**
 * Component inspired from MUI example (See: https://mui.com/material-ui/react-autocomplete/#virtualization)
 */
const LISTBOX_PADDING = 8;
/**
 *
 * @param {ListChildComponentProps} props component props
 * @returns {ReactElement} renderRow
 */
function renderRow(props) {
    const { data, index, style } = props;
    const dataSet = data[index];
    const inlineStyle = {
        ...style,
        top: style.top + LISTBOX_PADDING,
    };
    if ("group" in dataSet) {
        return (_jsx(ListSubheader, { component: "div", style: inlineStyle, children: dataSet.group }, dataSet.key));
    }
    return (_jsx(Typography, { component: "li", ...dataSet[0], noWrap: true, style: inlineStyle, children: dataSet[1] }));
}
const OuterElementContext = createContext({});
const OuterElementType = forwardRef((props, ref) => {
    const outerProps = useContext(OuterElementContext);
    return _jsx("div", { ref: ref, ...props, ...outerProps });
});
OuterElementType.displayName = "OuterElementType";
/**
 *
 * @param {unknown} data Data
 * @returns {MutableRefObject<VariableSizeList>} useResetCache
 */
function useResetCache(data) {
    const ref = useRef(null);
    useEffect(() => {
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [data]);
    return ref;
}
/**
 *
 * @param {unknown} obj obj to check
 * @returns {obj is { "aria-selected": boolean }} Has aria-selected
 */
function hasAriaSelected(obj) {
    if (typeof obj === "object" && obj !== null && "aria-selected" in obj)
        return true;
    else
        return false;
}
const ListboxComponent = forwardRef(function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData = [];
    children.forEach((item) => {
        itemData.push(item);
        itemData.push(...Array.from(item.children || []));
    });
    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up("sm"), {
        noSsr: true,
    });
    const itemCount = itemData.length;
    const itemSize = smUp ? 36 : 48;
    const getChildSize = (child) => {
        if (typeof child === "object" && child !== null)
            if ("group" in child) {
                return 48;
            }
        return itemSize;
    };
    const getHeight = () => {
        if (itemCount > 8) {
            return 8 * itemSize;
        }
        return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };
    const gridRef = useResetCache(itemCount);
    const selectedItem = useMemo(() => children.find((item) => {
        if (hasAriaSelected(item[0])) {
            if (item[0]["aria-selected"] === true)
                return true;
            else
                return false;
        }
        else
            return false;
    }), [children]);
    return (_jsx("div", { ref: ref, children: _jsx(OuterElementContext.Provider, { value: other, children: _jsx(VariableSizeList, { itemData: itemData, height: getHeight() + 2 * LISTBOX_PADDING, width: "100%", ref: gridRef, outerElementType: OuterElementType, innerElementType: "ul", itemSize: (index) => getChildSize(itemData[index]), overscanCount: 5, initialScrollOffset: selectedItem ? itemData.indexOf(selectedItem) * itemSize : 0, itemCount: itemCount, children: renderRow }) }) }));
});
const StyledPopper = styled(Popper)({
    [`& .${autocompleteClasses.listbox}`]: {
        boxSizing: "border-box",
        "& ul": {
            padding: 0,
            margin: 0,
        },
    },
});
/**
 *
 * @param {AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>} props component props
 * @returns {ReactElement} NerdVirtualizedAutocomplete
 */
export default function NerdVirtualizedAutocomplete(props) {
    const autoCompleteProps = useMemo(() => {
        const _autoCompleteProps = { ...props };
        delete _autoCompleteProps.disableVirtualization;
        return _autoCompleteProps;
    }, [props]);
    if (props.disableVirtualization) {
        return (_jsx(Autocomplete, { sx: { width: 300 }, renderOption: (props, option) => (_jsx(MenuItem, { ...props, children: option })), ...autoCompleteProps }));
    }
    else
        return (_jsx(Autocomplete, { sx: { width: 300 }, disableListWrap: true, renderOption: (props, option) => [props, option], renderGroup: (params) => params, PopperComponent: StyledPopper, ListboxComponent: ListboxComponent, ...autoCompleteProps }));
}
